import { CLIENT_LOGIN_PATH, CLIENT_OTP_RESEND_PATH, CLIENT_OTP_VERIFY_PATH } from '@/config/env';
import { DEVICE_FINGERPRINT_HEADER, getDeviceFingerprint } from '@/lib/auth/deviceFingerprint';
import { describeLoginError } from '@/lib/auth/errors';
import { toOtpChallenge, toSession, type Credentials, type OtpChallenge, type Session } from '@/lib/auth/session';

import { ApiError, request } from './httpClient';

export type { Credentials, OtpChallenge, Session };
export type { AuthUser } from '@/lib/auth/session';
export { describeLoginError };

/** Where a sign-in attempt landed: fully signed in, or waiting for the SMS code. */
export type AuthStep = { kind: 'signed-in'; session: Session } | { kind: 'otp'; challenge: OtpChallenge };

export function getLoginEndpoint(): string {
  return CLIENT_LOGIN_PATH;
}

/** Same fingerprint on every step — the backend ties the OTP to the device that asked for it. */
function postAuth(url: string, body: Record<string, unknown>): Promise<unknown> {
  return request<unknown>(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { [DEVICE_FINGERPRINT_HEADER]: getDeviceFingerprint() },
    body,
  });
}

function toAuthStep(payload: unknown): AuthStep {
  const challenge = toOtpChallenge(payload);
  if (challenge) return { kind: 'otp', challenge };

  const session = toSession(payload);
  if (!session.token) throw new ApiError('Unexpected login response. Please try again.', 502, payload);
  return { kind: 'signed-in', session };
}

/**
 * Signs in via the same-origin proxy, which forwards to the tablet-app
 * SimpleJWT endpoint (`/api/token/` with `{ login, password }`).
 */
export async function login({ identifier, password }: Credentials): Promise<AuthStep> {
  return toAuthStep(await postAuth(getLoginEndpoint(), { login: identifier, password }));
}

/** Second step for accounts with SMS 2FA. */
export async function verifyOtp(otpToken: string, code: string): Promise<AuthStep> {
  return toAuthStep(await postAuth(CLIENT_OTP_VERIFY_PATH, { otp_token: otpToken, code: code.replace(/\s+/g, '') }));
}

/** Texts a fresh code; returns the (possibly renewed) challenge. */
export async function resendOtp(otpToken: string): Promise<OtpChallenge> {
  const payload = await postAuth(CLIENT_OTP_RESEND_PATH, { otp_token: otpToken });
  const challenge = toOtpChallenge(payload);
  if (!challenge) throw new ApiError('Could not send a new code. Please sign in again.', 502, payload);
  return challenge;
}
